import React, { useState, useContext, useEffect } from 'react';
import { motion } from 'framer-motion';
import LanguageContext from '../contexts/LanguageContext';
import { 
  getFilteredProducts, 
  getCategoriesWithCounts, 
  getAllTags,
  isFeatureEnabled,
  getDisplayConfig 
} from '../services/productService';
import ProductCard from '../components/products/ProductCard';
import './Products.scss';

const Products = () => {
  const { language } = useContext(LanguageContext);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [filters, setFilters] = useState({
    category: '',
    type: '',
    featured: false,
    new: false,
    tags: [],
    search: ''
  });
  const [displayConfig, setDisplayConfig] = useState({});
  const [activeView, setActiveView] = useState('grid');
  const [sortBy, setSortBy] = useState('priority');

  // Load products and configuration
  useEffect(() => {
    const loadData = () => {
      // Get filtered products
      const filteredProducts = getFilteredProducts(filters, language);
      setProducts(filteredProducts);
      
      // Get categories
      const categoryList = getCategoriesWithCounts(language);
      setCategories(categoryList);
      
      // Get tags
      const tagList = getAllTags(language);
      setTags(tagList);
      
      // Get display configuration
      const config = getDisplayConfig(language);
      setDisplayConfig(config);
      setActiveView(config.layout || 'grid');
    };
    
    loadData();
  }, [language, filters]);

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => {
      if (filterName === 'category' && prev.category === value) {
        return { ...prev, category: '' }; // Toggle off if same category clicked
      }
      if (filterName === 'type' && prev.type === value) {
        return { ...prev, type: '' };
      }
      if (filterName === 'featured' || filterName === 'new') {
        return { ...prev, [filterName]: !prev[filterName] };
      }
      if (filterName === 'tags') {
        const newTags = prev.tags.includes(value) 
          ? prev.tags.filter(tag => tag !== value)
          : [...prev.tags, value];
        return { ...prev, tags: newTags };
      }
      return { ...prev, [filterName]: value };
    });
  };

  // Handle search
  const handleSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setFilters(prev => ({ ...prev, search: searchTerm }));
  };

  // Apply search filter to products
  const filteredProducts = products.filter(product => {
    if (!filters.search) return true;
    
    const searchTerm = filters.search.toLowerCase();
    return (
      product.name.toLowerCase().includes(searchTerm) ||
      product.shortDescription.toLowerCase().includes(searchTerm) ||
      product.description.toLowerCase().includes(searchTerm) ||
      product.tags.some(tag => tag.toLowerCase().includes(searchTerm))
    );
  });

  // Apply sorting
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'priority':
        return a.visibility.priority - b.visibility.priority;
      case 'name':
        return a.name.localeCompare(b.name);
      case 'featured':
        return (b.visibility.featured ? 1 : 0) - (a.visibility.featured ? 1 : 0);
      case 'new':
        return (b.visibility.new ? 1 : 0) - (a.visibility.new ? 1 : 0);
      default:
        return a.visibility.priority - b.visibility.priority;
    }
  });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <motion.div
      className="products-page"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Hero Section */}
      <section className="products-hero">
        <div className="container">
          <motion.div className="hero-content" variants={itemVariants}>
            <h1 className="page-title">Our Products & Showcases</h1>
            <p className="hero-subtitle">
              Discover our comprehensive range of AI-powered solutions and successful project implementations
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container">
        <div className="products-layout">
          {/* Sidebar Filters */}
          {isFeatureEnabled('enableFiltering', language) && (
            <motion.aside className="products-sidebar" variants={itemVariants}>
              <div className="sidebar-section">
                <h3 className="sidebar-title">Filters</h3>
                
                {/* Search */}
                {isFeatureEnabled('enableSearch', language) && (
                  <div className="filter-group">
                    <label className="filter-label">Search</label>
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search products..."
                      value={filters.search}
                      onChange={handleSearch}
                    />
                  </div>
                )}

                {/* Categories */}
                {isFeatureEnabled('enableCategories', language) && categories.length > 0 && (
                  <div className="filter-group">
                    <label className="filter-label">Categories</label>
                    <div className="category-list">
                      <button
                        className={`category-item ${!filters.category ? 'active' : ''}`}
                        onClick={() => handleFilterChange('category', '')}
                      >
                        <span className="category-name">All Categories</span>
                        <span className="category-count">{products.length}</span>
                      </button>
                      {categories.map(category => (
                        <button
                          key={category.id}
                          className={`category-item ${filters.category === category.id ? 'active' : ''}`}
                          onClick={() => handleFilterChange('category', category.id)}
                        >
                          <span className="category-name">{category.name}</span>
                          <span className="category-count">{category.count}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Product Types */}
                <div className="filter-group">
                  <label className="filter-label">Product Type</label>
                  <div className="type-list">
                    <button
                      className={`type-item ${!filters.type ? 'active' : ''}`}
                      onClick={() => handleFilterChange('type', '')}
                    >
                      All Types
                    </button>
                    <button
                      className={`type-item ${filters.type === 'product' ? 'active' : ''}`}
                      onClick={() => handleFilterChange('type', 'product')}
                    >
                      Products
                    </button>
                    <button
                      className={`type-item ${filters.type === 'showcase' ? 'active' : ''}`}
                      onClick={() => handleFilterChange('type', 'showcase')}
                    >
                      Showcases
                    </button>
                  </div>
                </div>

                {/* Featured & New */}
                <div className="filter-group">
                  <label className="filter-label">Status</label>
                  <div className="status-filters">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={filters.featured}
                        onChange={() => handleFilterChange('featured')}
                      />
                      <span className="checkbox-custom"></span>
                      Featured Only
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={filters.new}
                        onChange={() => handleFilterChange('new')}
                      />
                      <span className="checkbox-custom"></span>
                      New Products
                    </label>
                  </div>
                </div>

                {/* Tags */}
                {isFeatureEnabled('enableTags', language) && tags.length > 0 && (
                  <div className="filter-group">
                    <label className="filter-label">Tags</label>
                    <div className="tags-list">
                      {tags.slice(0, 10).map(tag => (
                        <button
                          key={tag}
                          className={`tag-item ${filters.tags.includes(tag) ? 'active' : ''}`}
                          onClick={() => handleFilterChange('tags', tag)}
                        >
                          {tag}
                        </button>
                      ))}
                      {tags.length > 10 && (
                        <div className="tags-more">
                          +{tags.length - 10} more tags
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Clear Filters */}
                {(filters.category || filters.type || filters.featured || filters.new || filters.tags.length > 0 || filters.search) && (
                  <button
                    className="btn btn-outline btn-block"
                    onClick={() => setFilters({
                      category: '',
                      type: '',
                      featured: false,
                      new: false,
                      tags: [],
                      search: ''
                    })}
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            </motion.aside>
          )}

          {/* Main Content */}
          <main className="products-main">
            {/* Controls Bar */}
            <motion.div className="products-controls" variants={itemVariants}>
              <div className="controls-left">
                <span className="results-count">
                  {sortedProducts.length} {sortedProducts.length === 1 ? 'product' : 'products'} found
                </span>
              </div>
              
              <div className="controls-right">
                {/* View Toggle */}
                <div className="view-toggle">
                  <button
                    className={`view-btn ${activeView === 'grid' ? 'active' : ''}`}
                    onClick={() => setActiveView('grid')}
                    title="Grid View"
                  >
                    <span className="view-icon">◼◼</span>
                  </button>
                  <button
                    className={`view-btn ${activeView === 'list' ? 'active' : ''}`}
                    onClick={() => setActiveView('list')}
                    title="List View"
                  >
                    <span className="view-icon">≡</span>
                  </button>
                </div>

                {/* Sort Dropdown */}
                {isFeatureEnabled('enableSorting', language) && (
                  <div className="sort-dropdown">
                    <label className="sort-label">Sort by:</label>
                    <select
                      className="sort-select"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="priority">Priority</option>
                      <option value="name">Name</option>
                      <option value="featured">Featured</option>
                      <option value="new">Newest</option>
                    </select>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Products Grid/List */}
            {sortedProducts.length === 0 ? (
              <motion.div className="no-results" variants={itemVariants}>
                <div className="no-results-icon">🔍</div>
                <h3>No products found</h3>
                <p>Try adjusting your filters or search terms</p>
                <button
                  className="btn"
                  onClick={() => setFilters({
                    category: '',
                    type: '',
                    featured: false,
                    new: false,
                    tags: [],
                    search: ''
                  })}
                >
                  Clear All Filters
                </button>
              </motion.div>
            ) : (
              <motion.div 
                className={`products-container ${activeView}-view`}
                variants={containerVariants}
              >
                {sortedProducts.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    context="catalog"
                    showImage={displayConfig.showImages}
                    showDescription={displayConfig.showDescriptions}
                    showPrice={displayConfig.showPrices}
                    showTags={isFeatureEnabled('enableTags', language)}
                    showFeatures={true}
                  />
                ))}
              </motion.div>
            )}

            {/* Pagination */}
            {isFeatureEnabled('enablePagination', language) && sortedProducts.length > (displayConfig.itemsPerPage || 12) && (
              <motion.div className="products-pagination" variants={itemVariants}>
                <button className="pagination-btn" disabled>
                  ← Previous
                </button>
                <div className="pagination-pages">
                  <span className="page-active">1</span>
                  <span className="page">2</span>
                  <span className="page">3</span>
                  <span className="page-dots">...</span>
                  <span className="page">10</span>
                </div>
                <button className="pagination-btn">
                  Next →
                </button>
              </motion.div>
            )}
          </main>
        </div>
      </div>
    </motion.div>
  );
};

export default Products;